/**
 * The live turn (D3) — the only state in this app that is *not* server state.
 *
 * It exists for tens of seconds and then stops existing: the stream writes here while it
 * runs, and on the terminal `chapter` event `commitTurn` moves it into the Query cache and
 * `reset()` clears it. That is the rule the whole state design turns on — one code path
 * turns ephemeral stream state into durable server state, and a reload mid-turn recovers by
 * refetching rather than by reconstructing.
 *
 * **One store, not one per turn.** The design said "one store instance per turn; reset on
 * commit" — with one story in play at a time those are the same object, and a module-level
 * store means no context plumbing for something every panel reads. `begin()` resets, so a
 * turn can never inherit the previous turn's narration.
 *
 * Nothing here knows about fetch, React, or the Query cache. It is a reducer with a
 * subscription, which is what makes the event sequence testable without a network.
 */
import { create } from 'zustand';
import type { ApiError } from '@/api/client';
import type { Change, Progress, TerminalTurnEvent, TurnEvent } from '@/api/events';
import type { ReconcileResult } from '@/turn/reconcile';

export type TurnStatus =
  /** No turn of ours is running. */
  | 'idle'
  /** The POST is open. Do not submit another action. */
  | 'streaming'
  /** `chapter` arrived. Lives for the moment between the terminal event and the commit. */
  | 'succeeded'
  /** Terminal `error`, a rejected POST, or a dropped stream — `error` says which. */
  | 'failed';

export interface TurnState {
  status: TurnStatus;
  storyId: string | null;
  /**
   * The optimistic echo: what the player typed, rendered in the feed as pending from
   * the instant they submit, reconciled by the `chapter` event. `null` on the opening turn.
   */
  pendingAction: string | null;
  /** From `turn.started`, or from a 409's `active_turn`. */
  chapterNumber: number | null;
  /** The last `progress` event. `label` is in the game's voice and is what the feed shows. */
  phase: Progress | null;
  /** Appended `narration.delta` text. Buffering for render is the renderer's job. */
  narration: string;
  /** Every `change`, in arrival order — most of them land during the silent Phase A. */
  changes: Change[];
  /** From `death`, which arrives *before* `chapter`, never instead of it. */
  causeOfDeath: string | null;
  startedAt: number | null;
  /** Last byte of *anything*, heartbeat included. Stall detection reads it (D4). */
  lastByteAt: number | null;
  terminal: TerminalTurnEvent | null;
  error: ApiError | null;
  /**
   * What the server said when asked whether a turn we lost sight of is still running
   * (`reconcile.ts`). Null until something goes wrong, and the difference between
   * *"still resolving — do not act"* and *"it never ran — go ahead"*.
   */
  recovery: ReconcileResult | null;
}

export interface TurnActions {
  begin(input: { storyId: string; action: string | null; at?: number }): void;
  /** Any byte arrived — including a `: ping` that carries no event. */
  markActivity(at?: number): void;
  apply(event: TurnEvent, at?: number): void;
  fail(error: ApiError, at?: number): void;
  /** The answer from `reconcileTurn`, recorded so the UI and the poll both read one field. */
  noteRecovery(result: ReconcileResult): void;
  /** Called by the commit transition, after the cache has the chapter. */
  reset(): void;
}

export type TurnStore = TurnState & TurnActions;

/** The zero value, and what every consumer sees when no turn of theirs is running. */
export const IDLE_TURN: TurnState = {
  status: 'idle',
  storyId: null,
  pendingAction: null,
  chapterNumber: null,
  phase: null,
  narration: '',
  changes: [],
  causeOfDeath: null,
  startedAt: null,
  lastByteAt: null,
  terminal: null,
  error: null,
  recovery: null,
};

export const useTurnStore = create<TurnStore>()((set) => ({
  ...IDLE_TURN,

  begin: ({ storyId, action, at = Date.now() }) =>
    set({
      ...IDLE_TURN,
      status: 'streaming',
      storyId,
      pendingAction: action,
      startedAt: at,
      lastByteAt: at,
    }),

  markActivity: (at = Date.now()) => set({ lastByteAt: at }),

  apply: (event, at = Date.now()) =>
    set((state) => {
      const seen = { lastByteAt: at };
      switch (event.event) {
        case 'turn.started':
          return { ...seen, chapterNumber: event.data.chapter_number };
        case 'progress':
          return { ...seen, phase: event.data };
        case 'change':
          return { ...seen, changes: [...state.changes, event.data] };
        case 'narration.delta':
          return { ...seen, narration: state.narration + event.data.text };
        case 'death':
          return { ...seen, causeOfDeath: event.data.cause_of_death };
        case 'chapter':
          return {
            ...seen,
            status: 'succeeded' as const,
            terminal: event,
            chapterNumber: event.data.chapter_number,
            causeOfDeath: event.data.cause_of_death ?? state.causeOfDeath,
            // The narration this client rendered came from the deltas; the terminal event
            // carries the whole thing. Taking the authoritative copy makes a dropped delta
            // a brief gap rather than a permanently wrong chapter.
            narration: event.data.narration,
            changes: event.data.changes,
          };
        case 'error':
          // Recorded, but the status transition belongs to `fail()`: `runTurn` throws this
          // envelope as an `ApiError`, and every failure — wire, network or terminal —
          // lands in one place rather than two that must agree.
          return { ...seen, terminal: event };
      }
    }),

  fail: (error, at = Date.now()) =>
    set((state) => ({
      status: 'failed',
      error,
      lastByteAt: at,
      // A 409 is the one failure that tells us which chapter is being written, and that
      // number is what the recovery poll needs to recognise the turn landing (P3).
      chapterNumber: state.chapterNumber ?? error.activeTurn?.chapter_number ?? null,
    })),

  noteRecovery: (result) => set({ recovery: result }),

  reset: () => set({ ...IDLE_TURN }),
}));

/**
 * Whether the store is holding a turn that may still be resolving on the server.
 *
 * This is the question the recovery paths ask — visibility change, a 409, a dropped
 * stream — and it is *not* "did it fail". A `refused` turn wrote nothing and is over; an
 * interrupted stream is a turn still spending money somewhere.
 */
export function mayStillBeRunning(state: TurnState): boolean {
  if (state.status === 'streaming') return true;
  if (state.status !== 'failed' || !state.error) return false;
  // The server has been asked and answered: no turn running, no new chapter. Asking again
  // on a timer would be a poll that can never change its mind.
  if (state.recovery === 'lost') return false;
  return (
    state.error.code === 'stream_interrupted' ||
    state.error.code === 'turn_in_progress' ||
    state.error.code === 'network'
  );
}
